/**
 * Helper utilities for ajvErrorMapping logic.
 */
import Ajv from "ajv";

export function getFieldScheme(ajvError, schema) {
    let { instancePath } = ajvError;
    if (instancePath == null || !schema || !schema.properties) {
        return {};
    }

    if (ajvError.params?.missingProperty != null) {
        instancePath = `${instancePath}/${ajvError.params.missingProperty}`;
    }

    // Remove leading slash and get the field name
    const fieldName = instancePath.replace(/^\//, '');
    
    // For root level properties
    if (schema.properties[fieldName]) {
        return schema.properties[fieldName];
    }

    // For nested properties
    const pathParts = fieldName.split('/');
    let currentSchema = schema;

    for (const part of pathParts) {
        if (!part) continue;
        
        if (currentSchema.properties && currentSchema.properties[part]) {
            currentSchema = currentSchema.properties[part];
        } else if (currentSchema.items) {
            // Handle array items
            currentSchema = currentSchema.items;
        } else {
            return {};
        }
    }

    return currentSchema;
}

export function getFieldTitle(ajvError, schema) {
    const {instancePath} = ajvError;
    const fieldScheme = getFieldScheme(ajvError, schema);
    
    // If we have a title in the schema, use it
    if (fieldScheme?.title) {
        return fieldScheme.title;
    }

    // Otherwise, get the field name from the path
    if (instancePath) {
        // Remove leading slash if present
        const path = instancePath.startsWith('/') ? instancePath.slice(1) : instancePath;
        const parts = path.split('/');
        // Get the last non-empty part
        return parts.filter(p => p).pop() || 'Field';
    }

    return 'Field';
}

export function replacePlaceholders(str, ...args) {
    if (!str) return '';
    return args.reduce((message, arg, index) => {
        return message.replaceAll(`{{${index}}}`, arg ?? '');
    }, str);
}

export function getMaxDecimalDigitsFromScheme(num) {
    const str = String(num);
    const dotIndex = str.indexOf('.');
    if (dotIndex === -1) return 0;
    return str.length - dotIndex - 1;
}

const ajv = new Ajv({ allErrors: true, verbose: true });
export function getRequiredFieldsBasedOnPayload(schema, data) {
    let requiredFields = [];

    // Handle allOf (recursively)
    if (schema.allOf && Array.isArray(schema.allOf)) {
        for (const subSchema of schema.allOf) {
            requiredFields = requiredFields.concat(
                getRequiredFieldsBasedOnPayload(subSchema, data)
            );
        }
    }

    // Handle if-then-else
    if (schema.if?.properties || schema.if?.not?.properties) {
        const partialSchema =
            schema.if?.properties != null ? schema.if : schema.if.not;
        const validate = ajv.compile(partialSchema);
        const isValid = validate(data);

        // When data is validated by schema.if.not, we should check schema.else
        const shouldCheckThen = schema.if?.properties == null ? !isValid : isValid;
        if (shouldCheckThen) {
            if (schema.then) {
                requiredFields = requiredFields.concat(
                    getRequiredFieldsBasedOnPayload(schema.then, data)
                );
            }
        } else if (schema.else) {
            requiredFields = requiredFields.concat(
                getRequiredFieldsBasedOnPayload(schema.else, data)
            );
        }
    }

    // Collect required fields at this level
    if (schema.required && Array.isArray(schema.required)) {
        requiredFields = requiredFields.concat(schema.required);
    }

    // Remove duplicates
    return Array.from(new Set(requiredFields));
}